/**
 * Collision resolution for a single moving icon against static obstacles.
 *
 * Used during drag to "snap" the dragged icon against the side of any other
 * icon whose AABB would overlap. Icons are treated as axis-aligned squares.
 *
 * The algorithm iteratively pushes the dragged icon out of the closest
 * overlapping obstacle along the axis of minimum penetration, until no
 * overlap remains or `maxIterations` is reached.
 */

export interface IconObstacle {
  /** World-space X of the obstacle centre. */
  x: number;
  /** World-space Y of the obstacle centre. */
  y: number;
  /** Half side length of the obstacle square (world units). */
  halfSize: number;
}

/**
 * @param desiredX   Cursor-driven target X (world units).
 * @param desiredY   Cursor-driven target Y (world units).
 * @param halfSize   Half side length of the dragged icon square (world units).
 * @param obstacles  All other icons' rects (must NOT include the dragged one).
 * @param maxIterations Safety cap (default 12).
 * @returns Adjusted position that does not overlap any obstacle. Equals
 *          `{ desiredX, desiredY }` when no constraint applies.
 */
export function resolveDragCollision(
  desiredX: number,
  desiredY: number,
  halfSize: number,
  obstacles: ReadonlyArray<IconObstacle>,
  maxIterations = 12,
): { x: number; y: number } {
  let x = desiredX;
  let y = desiredY;

  for (let iter = 0; iter < maxIterations; iter++) {
    let moved = false;

    for (const o of obstacles) {
      const halfSum = halfSize + o.halfSize;
      const dx = x - o.x;
      const dy = y - o.y;
      const overlapX = halfSum - Math.abs(dx);
      const overlapY = halfSum - Math.abs(dy);

      // No overlap on at least one axis → squares are separate.
      if (overlapX <= 0 || overlapY <= 0) continue;

      // Push along the axis with the smaller penetration (cheapest exit).
      // Add a 0.5px margin so the rects are strictly separate after rounding.
      if (overlapX <= overlapY) {
        const dir = dx === 0 ? 1 : Math.sign(dx);
        x += (overlapX + 0.5) * dir;
      } else {
        const dir = dy === 0 ? 1 : Math.sign(dy);
        y += (overlapY + 0.5) * dir;
      }
      moved = true;
    }

    if (!moved) break;
  }

  return { x, y };
}
